import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trainingService } from '../../services/trainingService';
import { courseService } from '../../services/courseService';
import { DataCacheContext } from '../../context/DataCacheContext';
import './EmployeeTrainings.css';

const EmployeeTrainings = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { matrix } = useContext(DataCacheContext);
  
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [assignedDate, setAssignedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expirationDate, setExpirationDate] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [extendLoading, setExtendLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [completedDate, setCompletedDate] = useState(new Date().toISOString().split('T')[0]);
  const [newExpirationDate, setNewExpirationDate] = useState('');
  const [certificateFile, setCertificateFile] = useState(null);
  const [uploadMethod, setUploadMethod] = useState('date');
  const [extendType, setExtendType] = useState('date');
  const [extendMonths, setExtendMonths] = useState(12);
  const [certificateNumber, setCertificateNumber] = useState('');
  const [modalMode, setModalMode] = useState('complete'); 
  const fetchTimeoutRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  }, []);

  // Проверка, является ли обучение бессрочным
  const isPerpetual = (periodicityMonths) => {
    return periodicityMonths === 0 || periodicityMonths === null || periodicityMonths === undefined;
  };

  // НОВАЯ ФУНКЦИЯ: Проверка, требуется ли пройти обучение (нет даты истечения)
  const isRequired = (expiresDate) => {
    return (!expiresDate || expiresDate === null);
  };

  const calculateDaysLeft = (expiresDateString, periodicityMonths) => {
    // Бессрочные обучения никогда не истекают
    if (isPerpetual(periodicityMonths)) {
      return Infinity;
    }
    if (!expiresDateString) return Infinity;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expireDate = new Date(expiresDateString);
    expireDate.setHours(0, 0, 0, 0);
    return Math.ceil((expireDate - today) / (1000 * 60 * 60 * 24));
  };

  const addMonths = (dateString, months) => {
    const date = dateString ? new Date(dateString) : new Date();
    date.setMonth(date.getMonth() + months);
    return date;
  };

  const getMonthsDeclension = (months) => {
    const lastDigit = months % 10;
    const lastTwoDigits = months % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return 'месяцев';
    }
    if (lastDigit === 1) {
      return 'месяц';
    }
    if (lastDigit >= 2 && lastDigit <= 4) {
      return 'месяца';
    }
    return 'месяцев';
  };

  const fetchEmployeeTrainings = useCallback(async () => {
    if (!employeeId) {
      setError('ID сотрудника не указан');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await trainingService.getByEmployee(employeeId);
      
      let trainingsArray = [];
      if (Array.isArray(response.trainings)) {
        trainingsArray = response.trainings;
      } else if (Array.isArray(response.data)) {
        trainingsArray = response.data;
      } else if (Array.isArray(response)) {
        trainingsArray = response;
      }
      
      const normalizedTrainings = trainingsArray.map(t => {
        const periodicityMonths = t.periodicity_months ?? t.periodicityMonths ?? null;
        const expiresDate = t.expiresDate || t.expiration_date || t.expires_date;
        const completedDateValue = t.completedDate || t.completed_date;
        const isPerpetualCourse = isPerpetual(periodicityMonths);
        const isRequiredCourse = isRequired(expiresDate);
        
        // ИСПОЛЬЗУЕМ СТАТУС ИЗ ОТВЕТА СЕРВЕРА
        const serverStatus = t.status || 'active';
        
        let daysLeft = Infinity;
        let status = serverStatus; // Берем статус с сервера
        
        // Если статус с сервера не указан или 'active', вычисляем сами
        if (!serverStatus || serverStatus === 'active') {
          if (completedDateValue) {
            status = 'completed';
          } else if (isRequiredCourse) {
            status = 'required';
          } else if (!isPerpetualCourse) {
            daysLeft = calculateDaysLeft(expiresDate, periodicityMonths);
            if (daysLeft <= 0) {
              status = 'expired';
            } else if (daysLeft <= 30) {
              status = 'warning';
            } else {
              status = 'completed';
            }
          }
        }
        
        // Для вычисления daysLeft используем существующую логику
        if (!isPerpetualCourse && expiresDate) {
          daysLeft = calculateDaysLeft(expiresDate, periodicityMonths);
        }
        
        return {
          id: t.id,
          name: t.name || t.course_name,
          course_id: t.course_id || t.courseId || t.courseId,
          periodicity_months: periodicityMonths,
          isPerpetual: isPerpetualCourse,
          isRequired: isRequiredCourse,
          assignedDate: t.assignedDate || t.assigned_date,
          completedDate: completedDateValue,
          expiresDate: expiresDate,
          certificateUrl: t.certificateUrl || t.certificate_url,
          certificateNumber: t.certificateNumber || t.certificate_number || null,
          regulatoryActs: t.regulatoryActs || t.regulatory_acts || null,
          daysLeft: daysLeft,
          status: status // Используем статус с сервера
        };
      });
      
      const total = normalizedTrainings.length;
      const completed = normalizedTrainings.filter(t => t.status === 'completed').length;
      const expired = normalizedTrainings.filter(t => t.status === 'expired').length;
      const required = normalizedTrainings.filter(t => t.status === 'required').length;
      // Для "истекающих" считаем те, у которых статус 'warning'
      const expiring = normalizedTrainings.filter(t => t.status === 'warning').length;
      
      // Активные - все остальные (кроме completed, expired, required, warning)
      const active = normalizedTrainings.filter(t => 
        t.status !== 'completed' && 
        t.status !== 'expired' && 
        t.status !== 'required' && 
        t.status !== 'warning'
      ).length;
      
      // Актуально пройденные (activeCompliant):
      // 1. Пройденные (completed) ИЛИ
      // 2. Активные (active) с датой истечения в будущем
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const activeCompliantCount = normalizedTrainings.filter(t => {
        if (t.status === 'completed') return true;
        if (t.status === 'active') {
          const expiresDateObj = t.expiresDate ? new Date(t.expiresDate) : null;
          return expiresDateObj && expiresDateObj >= today;
        }
        return false;
      }).length;
      
      const compliance = total > 0 ? (activeCompliantCount / total) >= 0.8 : false;
      
      const normalizedData = {
        employeeName: response.employeeName || response.employee_name || `Сотрудник №${employeeId}`,
        employeePosition: response.employeePosition || response.employee_position || '—',
        brigade: response.brigade || response.brigade_name || 'Не указана',
        statistics: {
          total: total,
          active: active,
          completed: completed,
          expired: expired,
          expiring: expiring,
          required: required,
          activeCompliant: activeCompliantCount,
          compliance: compliance
        },
        trainings: normalizedTrainings
      };
      
      setData(normalizedData);
    } catch (err) {
      console.error('Ошибка загрузки обучений:', err);
      
      let errorMessage = 'Не удалось загрузить данные об обучении';
      if (err.status === 401) {
        errorMessage = 'Необходимо авторизоваться. Пожалуйста, войдите в систему заново.';
      } else if (err.status === 404) {
        errorMessage = `Сотрудник с ID ${employeeId} не найден`;
      } else if (err.status === 500) {
        errorMessage = 'Ошибка сервера. Попробуйте позже.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [employeeId, matrix.lastUpdated]);

  const fetchEmployeeTrainingsDebounced = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }
    
    fetchTimeoutRef.current = setTimeout(() => {
      fetchEmployeeTrainings();
    }, 300);
  }, [fetchEmployeeTrainings]);

  useEffect(() => {
    fetchEmployeeTrainingsDebounced();
    
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [fetchEmployeeTrainingsDebounced, matrix.lastUpdated]);

  const getStatusClass = (training) => {
    return training.status || 'active';
  };

  const getStatusText = (training) => {
    const statusMap = {
      'completed': 'Пройдено',
      'required': 'Требуется пройти',
      'perpetual': 'Бессрочно',
      'expired': 'Просрочено',
      'warning': 'Истекает',
      'active': 'Активно'
    };
    return statusMap[training.status] || 'Активно';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

 const formatExpiresDate = (training) => {
  // Сначала проверяем бессрочность
  if (training.isPerpetual) {
    // Если бессрочное обучение пройдено - показываем "-"
    if (training.status === 'completed' || training.completedDate) {
      return '—';
    }
    // Если бессрочное и НЕ пройдено - "Требуется пройти"
    return 'Требуется пройти';
  }
  // Для срочных обучений
  if (training.isRequired) return 'Требуется пройти';
  return formatDate(training.expiresDate);
};

  // Обновленная функция для расчета процента на основе activeCompliant
  const getCompletedPercentage = () => {
    if (!data?.statistics) return 0;
    const total = data.statistics.total;
    const activeCompliant = data.statistics.activeCompliant || 0;
    if (total === 0) return 0;
    return Math.round((activeCompliant / total) * 100);
  };

  const handleOpenAssignModal = async () => {
    try {
      const response = await courseService.getAll();
      
      let coursesArray = [];
      if (Array.isArray(response) && response.length > 0 && response[0].courses) {
        coursesArray = response[0].courses;
      } else if (Array.isArray(response)) {
        coursesArray = response;
      } else if (response?.data && Array.isArray(response.data)) {
        coursesArray = response.data;
      } else if (response?.courses && Array.isArray(response.courses)) {
        coursesArray = response.courses;
      }
      
      if (coursesArray.length === 0) {
        showToast('Нет доступных курсов для назначения', 'error');
        return;
      }
      
      const existingCourseIds = new Set(data?.trainings?.map(t => t.course_id));
      const available = coursesArray.filter(c => !existingCourseIds.has(c.id));
      
      if (available.length === 0) {
        showToast('Все доступные курсы уже назначены сотруднику', 'warning');
        return;
      }
      
      setAvailableCourses(available);
      setShowAssignModal(true);
    } catch (err) {
      console.error('Ошибка загрузки курсов:', err);
      showToast(`Не удалось загрузить список курсов: ${err.message || 'Неизвестная ошибка'}`, 'error');
    }
  };

  const handleAssignTraining = async () => {
    if (!selectedCourse) {
      showToast('Пожалуйста, выберите курс', 'error');
      return;
    }
    
    setAssignLoading(true);
    try {
      const course = availableCourses.find(c => c.id === parseInt(selectedCourse));
      
      // Формируем базовый payload
      const payload = {
        employee_id: parseInt(employeeId),
        course_id: parseInt(selectedCourse),
        assigned_date: assignedDate
      };
      
      // Добавляем expiration_date ТОЛЬКО если поле заполнено и не пустое
      if (expirationDate && expirationDate.trim() !== '') {
        payload.expiration_date = expirationDate;
      }
      // Если expiration_date пустая - не включаем в payload (будет null на бэкенде)
      
      await trainingService.assign(payload);
      
      showToast(`Обучение "${course?.name}" успешно назначено сотруднику`, 'success');
      setShowAssignModal(false);
      setSelectedCourse('');
      setExpirationDate('');
      setAssignedDate(new Date().toISOString().split('T')[0]);
      await fetchEmployeeTrainings();
    } catch (err) {
      console.error('Ошибка назначения:', err);
      showToast(`Ошибка: ${err.message || 'Не удалось назначить обучение'}`, 'error');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleOpenCompleteModal = (training, mode = 'complete') => {
    setSelectedTraining(training);
    setCompletedDate(new Date().toISOString().split('T')[0]);
    setCertificateFile(null);
    setCertificateNumber(training.certificateNumber || '');
    setUploadMethod('date');
    setModalMode(mode);
    setShowCompleteModal(true);
  };

  const closeCompleteModal = () => {
    setShowCompleteModal(false);
    setModalMode('complete');
  };

  const handleCompleteTraining = async () => {
    if (!selectedTraining) return;
    
    setCompleteLoading(true);
    try {
      console.log(`Завершение обучения ID: ${selectedTraining.id}`);
      
      if (uploadMethod === 'file' && certificateFile) {
        const formData = new FormData();
        formData.append('certificate_file', certificateFile);
        formData.append('completed_date', completedDate);
        if (certificateNumber) formData.append('certificate_number', certificateNumber);
        
        await trainingService.uploadCertificate(selectedTraining.id, formData);
        showToast(`Обучение "${selectedTraining.name}" отмечено как пройденное, сертификат загружен. Статус: Пройдено`, 'success');
      } else {
        await trainingService.complete(selectedTraining.id, {
          completed_date: completedDate,
          certificate_number: certificateNumber
        });
        showToast(`Обучение "${selectedTraining.name}" отмечено как пройденное. Статус: Пройдено`, 'success');
      }
      
      closeCompleteModal();
      setSelectedTraining(null);
      setCertificateFile(null);
      setCertificateNumber('');
      await fetchEmployeeTrainings();
    } catch (err) {
      console.error('Ошибка завершения обучения:', err);
      showToast(`Ошибка: ${err.message || 'Не удалось отметить обучение'}`, 'error');
    } finally {
      setCompleteLoading(false);
    }
  };

  const handleOpenExtendModal = (training) => {
    setSelectedTraining(training);
    if (training.isRequired) {
      // Для обучений со статусом "Требуется пройти" предлагаем добавить дату истечения
      const defaultExpiration = new Date();
      defaultExpiration.setFullYear(defaultExpiration.getFullYear() + 1);
      setNewExpirationDate(defaultExpiration.toISOString().split('T')[0]);
      setExtendMonths(12);
      setExtendType('date');
    } else if (training.isPerpetual) {
      setExtendType('perpetual');
      setExtendMonths(0);
    } else {
      const currentExpiration = training.expiresDate ? new Date(training.expiresDate) : new Date();
      const defaultExpiration = new Date(currentExpiration);
      defaultExpiration.setFullYear(defaultExpiration.getFullYear() + 1);
      setNewExpirationDate(defaultExpiration.toISOString().split('T')[0]);
      setExtendMonths(12);
      setExtendType('date');
    }
    setShowExtendModal(true);
  };

  const handleExtendTraining = async () => {
    if (!selectedTraining) return;
    
    setExtendLoading(true);
    try {
      let options;
      
      if (extendType === 'perpetual') {
        options = { periodicity_months: 0 };
        showToast(`Обучение "${selectedTraining.name}" продлено как бессрочное. Статус: Активно`, 'success');
      } else if (extendType === 'months') {
        options = { months: extendMonths };
        showToast(`Срок действия обучения "${selectedTraining.name}" продлен на ${extendMonths} ${getMonthsDeclension(extendMonths)}. Статус: Активно`, 'success');
      } else {
        options = { new_expiration_date: newExpirationDate };
        showToast(`Срок действия обучения "${selectedTraining.name}" продлен до ${formatDate(newExpirationDate)}. Статус: Активно`, 'success');
      }
      
      await trainingService.extend(selectedTraining.id, options);
      
      setShowExtendModal(false);
      setSelectedTraining(null);
      await fetchEmployeeTrainings();
    } catch (err) {
      console.error('Ошибка продления:', err);
      showToast(`Ошибка: ${err.message || 'Не удалось продлить обучение'}`, 'error');
    } finally {
      setExtendLoading(false);
    }
  };

  const handleOpenDeleteModal = (training) => {
    setSelectedTraining(training);
    setShowDeleteModal(true);
  };

  const handleDeleteTraining = async () => {
    if (!selectedTraining) return;
    
    setDeleteLoading(true);
    try {
      console.log(`Удаление обучения ID: ${selectedTraining.id}, название: ${selectedTraining.name}`);
      await trainingService.delete(selectedTraining.id);
      
      showToast(`Обучение "${selectedTraining.name}" успешно удалено`, 'success');
      setShowDeleteModal(false);
      setSelectedTraining(null);
      await fetchEmployeeTrainings();
    } catch (err) {
      console.error('Ошибка удаления:', err);
      showToast(`Ошибка при удалении: ${err.message || 'Не удалось удалить обучение'}`, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="employee-trainings loading">
        <div className="spinner"></div>
        <p>Загрузка обучений...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="employee-trainings error">
        <div className="error-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p>{error}</p>
        <button className="btn-primary" onClick={fetchEmployeeTrainings}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
            <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
            <path d="M21 3v6h-6" />
          </svg>
          Повторить
        </button>
        <button className="btn-secondary" onClick={() => navigate(-1)} style={{ marginLeft: '10px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Вернуться назад
        </button>
      </div>
    );
  }

  const completedPercentage = getCompletedPercentage();
  const activeCompliantCount = data?.statistics?.activeCompliant || 0;

  return (
    <div className="employee-trainings">
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-icon">
            {toast.type === 'success' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : toast.type === 'warning' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}

      <div className="trainings-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Назад
        </button>
        
        <div className="employee-info">
          <h1>{data?.employeeName}</h1>
          <div className="employee-details">
            <span className="badge position">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
              Должность: {data?.employeePosition}
            </span>
            {data?.brigade && 
             data.brigade !== "Не указана" && 
             data.brigade !== "Без бригады" && (
              <span className="badge brigade">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                  <path d="M3 21h18" />
                  <path d="M5 21V7l8-4v18" />
                  <path d="M19 21V11l-6-4" />
                  <path d="M9 9h1" />
                  <path d="M9 13h1" />
                  <path d="M9 17h1" />
                </svg>
                Бригада: {data?.brigade}
              </span>
            )}
          </div>
        </div>

        <div className="header-compliance">
          <div className="header-compliance-chart">
            <svg viewBox="0 0 50 50" className="header-donut">
              <circle cx="25" cy="25" r="20" fill="none" stroke="rgba(102, 126, 234, 0.2)" strokeWidth="5" />
              <circle 
                cx="25" 
                cy="25" 
                r="20" 
                fill="none" 
                stroke="#667eea" 
                strokeWidth="5"
                strokeDasharray={`${2 * Math.PI * 20 * completedPercentage / 100} ${2 * Math.PI * 20 * (100 - completedPercentage) / 100}`}
                strokeDashoffset={2 * Math.PI * 20 * 0.25}
                strokeLinecap="round"
                transform="rotate(-90 25 25)"
                className="header-donut-progress"
              />
              <text x="25" y="22" textAnchor="middle" className="header-donut-percent">{completedPercentage}%</text>
              <text x="25" y="34" textAnchor="middle" className="header-donut-label">пройдено</text>
            </svg>
          </div>
          <div className="header-compliance-stats">
            <div className="header-compliance-value">{activeCompliantCount}</div>
            <div className="header-compliance-label">из {data?.statistics.total || 0} курсов</div>
          </div>
        </div>

        <button className="btn-assign" onClick={handleOpenAssignModal}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Назначить обучение
        </button>
        <button className="btn-refresh" onClick={fetchEmployeeTrainings}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
            <path d="M21 3v6h-6" />
          </svg>
        </button>
      </div>

      {/* Блок статистики */}
      {data?.statistics && (
        <div className="stats-cards">
          <div className="stat-card active">
            <div className="stat-value">{data.statistics.active}</div>
            <div className="stat-label">Активных</div>
          </div>
          <div className="stat-card expired">
            <div className="stat-value">{data.statistics.expired}</div>
            <div className="stat-label">Просрочено</div>
          </div>
          <div className="stat-card expiring">
            <div className="stat-value">{data.statistics.expiring}</div>
            <div className="stat-label">Истекают (30 дней)</div>
          </div>
          <div className="stat-card required">
            <div className="stat-value">{data.statistics.required || 0}</div>
            <div className="stat-label">Требуется пройти</div>
          </div>
        </div>
      )}

      <div className="trainings-table-wrapper">
        <table className="trainings-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название курса</th>
              <th>Периодичность</th>
              <th>Статус</th>
              <th>Дата назначения</th>
              <th>Дата прохождения</th>
              <th>Действует до</th>
              <th>№ удостоверения</th>
              <th>Сертификат</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {data?.trainings?.map((training) => (
              <tr key={training.id}>
                <td>{training.id}</td>
                <td className="course-name">{training.name}</td>
                <td>
                  {training.isPerpetual ? (
                    <span className="perpetual-badge">Бессрочно</span>
                  ) : training.periodicity_months ? (
                    <span className="periodicity-badge">{training.periodicity_months} мес.</span>
                  ) : (
                    <span className="no-data">—</span>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${getStatusClass(training)}`}>
                    {getStatusText(training)}
                  </span>
                </td>
                <td>{formatDate(training.assignedDate)}</td>
                <td>{formatDate(training.completedDate)}</td>
                <td className="expires-date-cell">
                  <div className="expires-date-wrapper">
                    <span>{formatExpiresDate(training)}</span>
                    {/* Кнопка для изменения даты "Действует до" */}
                    <button 
                      className="edit-expires-btn"
                      onClick={() => handleOpenCompleteModal(training, 'expiration')}
                      title="Изменить дату прохождения"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                        <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td className="certificate-number-cell">
                  {training.certificateNumber ? (
                    <span className="certificate-number">{training.certificateNumber}</span>
                  ) : (
                    <span className="no-data">—</span>
                  )}
                </td>
                <td className="certificate-cell">
                  {training.certificateUrl ? (
                    <a href={training.certificateUrl} target="_blank" rel="noopener noreferrer" className="certificate-link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                        <polyline points="13 2 13 9 20 9" />
                      </svg>
                      Скачать
                    </a>
                  ) : (
                    <span className="no-certificate">—</span>
                  )}
                </td>
                <td className="actions-cell">
                  {/* Кнопка "Отметить как пройденное" для всех не пройденных обучений */}
                  {!training.completedDate && (
                    <button 
                      className="icon-btn complete" 
                      onClick={() => handleOpenCompleteModal(training)}
                      title="Отметить как пройденное"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </button>
                  )}
                  {/* Кнопка продления для всех обучений */}
                  <button 
                    className="icon-btn extend" 
                    onClick={() => handleOpenExtendModal(training)}
                    title={training.isRequired ? "Установить срок действия" : training.isPerpetual ? "Оставить бессрочным" : "Продлить обучение"}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
                      <path d="M21 3v6h-6" />
                    </svg>
                  </button>
                  <button 
                    className="icon-btn delete" 
                    onClick={() => handleOpenDeleteModal(training)}
                    title="Удалить обучение"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(!data?.trainings || data.trainings.length === 0) && (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
            <div className="empty-text">Нет назначенных обучений</div>
            <div className="empty-subtext">У сотрудника пока нет курсов</div>
          </div>
        )}
      </div>

      {/* Модальное окно назначения обучения */}
      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Назначить обучение</h3>
              <button className="modal-close" onClick={() => setShowAssignModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Курс *</label>
                <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} className="form-select">
                  <option value="">Выберите курс</option>
                  {availableCourses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name} {course.periodicity_months ? `(перерыв: ${course.periodicity_months} мес.)` : '(Бессрочно)'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Дата назначения</label>
                <input type="date" value={assignedDate} onChange={(e) => setAssignedDate(e.target.value)} className="form-input" />
              </div>

              <div className="form-group">
                <label>Пройти до (дата истечения)</label>
                <input 
                  type="date" 
                  value={expirationDate} 
                  onChange={(e) => setExpirationDate(e.target.value)} 
                  className="form-input" 
                  min={assignedDate}
                />
                <small className="form-hint">Необязательное поле. Если не указано, обучение будет считаться бессрочным.</small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAssignModal(false)}>Отмена</button>
              <button className="btn-submit" onClick={handleAssignTraining} disabled={assignLoading}>
                {assignLoading ? 'Назначение...' : 'Назначить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно завершения обучения */}
      {showCompleteModal && selectedTraining && (
        <div className="modal-overlay" onClick={closeCompleteModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'expiration' ? 'Изменить дату истечения' : 'Отметить обучение как пройденное'}</h3>
              <button className="modal-close" onClick={closeCompleteModal}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Курс:</strong> {selectedTraining.name}</p>
              <p><strong>Текущий статус:</strong> {getStatusText(selectedTraining)}</p>
              {selectedTraining.isRequired && (
                <p style={{ 
                  background: '#fef3c7', 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  fontSize: '13px',
                  marginBottom: '16px',
                  color: '#92400e'
                }}>
                  <strong>Информация:</strong> Это обучение требует прохождения. После отметки о прохождении оно будет считаться пройденным.
                </p>
              )}
              {selectedTraining.isPerpetual && (
                <p style={{ 
                  background: '#e0e7ff', 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  fontSize: '13px',
                  marginBottom: '16px',
                  color: '#3730a3'
                }}>
                  <strong>Информация:</strong> Это бессрочное обучение. После отметки о прохождении оно будет считаться пройденным навсегда.
                </p>
              )}
              <p style={{ 
                background: '#e0e7ff', 
                padding: '8px 12px', 
                borderRadius: '8px', 
                fontSize: '13px',
                marginBottom: '16px',
                color: '#3730a3'
              }}>
                <strong>После сохранения статус изменится на:</strong> Пройдено ✓
              </p>
              
              <div className="form-group">
                <label>№ удостоверения</label>
                <input 
                  type="text" 
                  value={certificateNumber}
                  onChange={(e) => setCertificateNumber(e.target.value)}
                  className="form-input"
                  placeholder="Введите номер удостоверения"
                />
                <small className="form-hint">Укажите номер удостоверения (если есть)</small>
              </div>
              
              <div className="form-group">
                <label>Способ отметки</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input type="radio" value="date" checked={uploadMethod === 'date'} onChange={() => setUploadMethod('date')} />
                    Только дата прохождения
                  </label>
                  <label className="radio-label">
                    <input type="radio" value="file" checked={uploadMethod === 'file'} onChange={() => setUploadMethod('file')} />
                    Загрузить сертификат
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>{modalMode === 'expiration' ? 'Дата прохождения *' : 'Дата прохождения *'}</label>
                <input type="date" value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} className="form-input" />
              </div>

              {uploadMethod === 'file' && (
                <div className="form-group">
                  <label>Файл сертификата *</label>
                  <input type="file" accept=".pdf,.jpg,.png" onChange={(e) => setCertificateFile(e.target.files[0])} className="form-input" />
                  <small className="form-hint">Поддерживаемые форматы: PDF, JPG, PNG</small>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeCompleteModal}>Отмена</button>
              <button className="btn-submit" onClick={handleCompleteTraining} disabled={completeLoading}>
                {completeLoading ? 'Сохранение...' : 'Отметить как пройденное'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно продления обучения */}
      {showExtendModal && selectedTraining && (
        <div className="modal-overlay" onClick={() => setShowExtendModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedTraining.isRequired ? 'Установить срок действия' : 'Продлить обучение'}</h3>
              <button className="modal-close" onClick={() => setShowExtendModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Курс:</strong> {selectedTraining.name}</p>
              <p><strong>Текущий статус:</strong> {getStatusText(selectedTraining)}</p>
              {!selectedTraining.isRequired && !selectedTraining.isPerpetual && (
                <p><strong>Текущая дата истечения:</strong> {formatDate(selectedTraining.expiresDate)}</p>
              )}
              {selectedTraining.isRequired && (
                <p style={{ 
                  background: '#fef3c7', 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  fontSize: '13px',
                  marginBottom: '16px',
                  color: '#92400e'
                }}>
                  <strong>Информация:</strong> Для этого обучения не установлен срок действия. Укажите дату истечения или сделайте его бессрочным.
                </p>
              )}
              {selectedTraining.isPerpetual && !selectedTraining.isRequired && (
                <p style={{ 
                  background: '#e0e7ff', 
                  padding: '8px 12px', 
                  borderRadius: '8px', 
                  fontSize: '13px',
                  marginBottom: '16px',
                  color: '#3730a3'
                }}>
                  <strong>Информация:</strong> Это бессрочное обучение. Оно не требует продления, но вы можете изменить его статус на срочное.
                </p>
              )}
              <p style={{ 
                background: '#e0e7ff', 
                padding: '8px 12px', 
                borderRadius: '8px', 
                fontSize: '13px',
                marginBottom: '16px',
                color: '#3730a3'
              }}>
                <strong>После сохранения статус изменится на:</strong> Активно ✓
              </p>
              
              <div className="form-group">
                <label>Способ {selectedTraining.isRequired ? 'установки срока' : 'продления'}</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      value="date" 
                      checked={extendType === 'date'} 
                      onChange={() => setExtendType('date')} 
                    />
                    {selectedTraining.isRequired ? 'Указать дату истечения' : 'Указать конкретную дату'}
                  </label>
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      value="months" 
                      checked={extendType === 'months'} 
                      onChange={() => setExtendType('months')} 
                    />
                    {selectedTraining.isRequired ? 'Добавить месяцы от текущей даты' : 'Добавить месяцы'}
                  </label>
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      value="perpetual" 
                      checked={extendType === 'perpetual'} 
                      onChange={() => setExtendType('perpetual')} 
                    />
                    Сделать бессрочным
                  </label>
                </div>
              </div>

              {extendType === 'date' && (
                <div className="form-group">
                  <label>Новая дата истечения *</label>
                  <input 
                    type="date" 
                    value={newExpirationDate} 
                    onChange={(e) => setNewExpirationDate(e.target.value)} 
                    className="form-input"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              )}

              {extendType === 'months' && (
                <div className="form-group">
                  <label>Количество месяцев *</label>
                  <div className="months-selector">
                    <input 
                      type="number" 
                      value={extendMonths} 
                      onChange={(e) => setExtendMonths(Math.max(1, parseInt(e.target.value) || 1))} 
                      className="form-input"
                      min="1"
                      max="60"
                      step="1"
                    />
                    <span className="months-hint">месяцев</span>
                  </div>
                  <small className="form-hint">
                    Новая дата истечения: {formatDate(addMonths(selectedTraining.expiresDate || new Date(), extendMonths))}
                  </small>
                </div>
              )}

              {extendType === 'perpetual' && (
                <div className="form-group">
                  <label>Бессрочное обучение</label>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
                    Обучение будет отмечено как бессрочное и не будет иметь даты истечения.
                    {selectedTraining.completedDate && ' После прохождения оно останется в статусе "Пройдено" навсегда.'}
                    {!selectedTraining.completedDate && ' Вы сможете отметить его как пройденное позже.'}
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowExtendModal(false)}>Отмена</button>
              <button className="btn-submit" onClick={handleExtendTraining} disabled={extendLoading}>
                {extendLoading ? 'Сохранение...' : (selectedTraining.isRequired ? 'Установить' : 'Продлить')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно удаления обучения */}
      {showDeleteModal && selectedTraining && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Удалить обучение</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Вы действительно хотите удалить обучение?</p>
              <p><strong>Курс:</strong> {selectedTraining.name}</p>
              <p style={{ color: '#ef4444', fontSize: '14px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                </svg>
                Это действие нельзя отменить. Все данные об обучении будут удалены.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Отмена</button>
              <button 
                className="btn-submit" 
                onClick={handleDeleteTraining} 
                disabled={deleteLoading}
                style={{ background: '#ef4444' }}
              >
                {deleteLoading ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeTrainings;